<template>
	<div class="content">
		<base-header class="pb-6">
			<div class="row align-items-center py-4">
				<div class="col-lg-6 col-7">
					<h6 class="h2 text-white d-inline-block mb-0">
						{{ $t("report_product_cost") }}
					</h6>
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</div>
			</div>
		</base-header>

		<div class="container-fluid mt--6">
			<card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
				<template slot="header">
					<!-- 1. filters -->
					<div class="form-group row align-items-center">
						<div class="col-md-4">
							<el-radio-group v-model="view" size="small" @change="onViewChange">
								<el-radio-button label="platform">{{ $t("cost_report.view_platform") }}</el-radio-button>
								<el-radio-button label="channel">{{ $t("cost_report.view_channel") }}</el-radio-button>
							</el-radio-group>
						</div>

						<!-- The two ends are Element pickers rather than flatpickr because
						     only these can grey out the days that would break the 7-day
						     rule (picker-options.disabledDate). value-format keeps the
						     model a plain YYYY-MM-DD — the API takes that, and a Date
						     object would reach the query string as an ISO timestamp. -->
						<div class="col-md-2">
							<el-date-picker
								v-model="timeFrom"
								type="date"
								size="small"
								class="w-100"
								:clearable="false"
								:editable="false"
								value-format="yyyy-MM-dd"
								format="yyyy-MM-dd"
								:placeholder="$t('time_from')"
								:picker-options="fromPickerOptions"
							>
							</el-date-picker>
						</div>

						<div class="col-md-2">
							<el-date-picker
								v-model="timeTo"
								type="date"
								size="small"
								class="w-100"
								:clearable="false"
								:editable="false"
								value-format="yyyy-MM-dd"
								format="yyyy-MM-dd"
								:placeholder="$t('time_to')"
								:picker-options="toPickerOptions"
							>
							</el-date-picker>
						</div>

						<div class="col-md-2">
							<base-button size="md" type="primary" :disabled="loading" @click="reload()">
								{{ $t("cost_report.search") }}
							</base-button>
						</div>

						<div class="col-md-4 text-right">
							<base-button size="md" type="neutral" :disabled="loading || exporting || backfilling" @click="exportExcel()">
								{{ $t("cost_report.export") }}
							</base-button>
							<base-button size="md" type="neutral" :disabled="loading || exporting || backfilling" @click="backfillCost()">
								{{ $t("cost_report.backfill") }}
							</base-button>
						</div>
					</div>

					<!-- the disclaimers §11.8 + §13.7 -->
					<div class="row">
						<div class="col-12">
							<small class="text-muted d-block">{{ $t("cost_report.cost_note") }}</small>
							<small class="text-muted d-block">{{ $t("cost_report.sales_note") }}</small>
							<small class="text-muted d-block">{{ $t("cost_report.dynamic_sku_note") }}</small>
							<small class="text-danger d-block" v-if="costColumnsMissing">
								{{ $t("cost_report.cost_columns_missing") }}
							</small>
						</div>
					</div>

					<!-- 2. drill-down breadcrumb -->
					<div class="row mt-2">
						<div class="col-12">
							<span v-for="(crumb, idx) in breadcrumb" :key="crumb.level">
								<span v-if="idx > 0" class="mx-1">&rsaquo;</span>
								<a href="#" v-if="!crumb.active" @click.prevent="goToLevel(crumb.level)">{{ crumb.label }}</a>
								<strong v-else>{{ crumb.label }}</strong>
							</span>
						</div>
					</div>

					<!-- 4. the summary strip -->
					<div class="row mt-3">
						<div :class="tileClass"><small class="text-muted d-block">{{ $t("cost_report.sales") }}</small><span class="h5">{{ money(total.sales) }}</span></div>
						<div :class="tileClass"><small class="text-muted d-block">{{ $t("cost_report.cost") }}</small><span class="h5">{{ money(total.cost) }}</span></div>
						<div :class="tileClass"><small class="text-muted d-block">{{ $t("cost_report.margin") }}</small><span class="h5">{{ money(total.margin) }}</span></div>
						<div :class="tileClass"><small class="text-muted d-block">{{ $t("cost_report.margin_pct") }}</small><span class="h5">{{ totalMarginPct }}%</span></div>
						<div :class="tileClass" v-if="expenseVisible" class="cost-tile-expense">
							<small class="text-muted d-block" :title="$t('cost_report.expense_note')">{{ $t("cost_report.expense") }} <i class="fa fa-info-circle"></i></small>
							<span class="h5 text-danger">{{ money(total.expense) }}</span>
						</div>
						<div :class="tileClass" v-if="expenseVisible" class="cost-tile-net">
							<small class="text-muted d-block">{{ $t("cost_report.net_profit") }}</small>
							<span class="h5" :class="totalNetProfit < 0 ? 'text-danger' : 'text-success'">{{ money(totalNetProfit) }}</span>
						</div>
						<div :class="tileClass">
							<small class="text-muted d-block">{{ $t("cost_report.missing_cost_sku") }}</small>
							<span class="h5" :class="{ 'text-warning': total.missing_cost_sku > 0 }">{{ total.missing_cost_sku }}</span>
						</div>
						<div :class="tileClass"><small class="text-muted d-block">{{ $t("cost_report.qty") }}</small><span class="h5">{{ total.qty }}</span></div>
					</div>
				</template>

				<!-- 3. the level's table -->
				<!-- :key on the wrapper remounts the table when the LOADED view changes
				     at the same level (platform ↔ channel): the รายจ่าย / กำไรสุทธิ
				     columns come and go with it, and Element UI 2.4 misplaces v-if
				     columns added to a live table (same bug as :key="level"). -->
				<div :key="'cost-view-' + loadedView" :class="{ 'cost-report-compact': expenseVisible }">
					<!-- :key="level" remounts the table per drill level. Element UI 2.4 inserts
					     v-if columns that appear later at the wrong index, so header and body
					     disagree (bo-stg 2026-09-23: "จำนวนชิ้น" showed the sales figure). -->
					<!-- no v-loading here any more: the blocking modal below is the one
					     busy indicator, and a spinner under a modal is invisible. -->
					<el-table :key="level" :data="rows" row-key="_rowKey" header-row-class-name="thead-light" @row-click="onRowClick">
						<el-table-column :label="$t('cost_report.name')" :min-width="expenseVisible ? 150 : 220">
							<div slot-scope="{ row }">
								{{ rowName(row) }}
								<!-- §13.6: a SKU row keyed 0 now shows the LINE's own name and
								     code, so it needs a tag to say where that identity came
								     from — and that it is a group, readable per line only in
								     the orders sheet. -->
								<el-tag v-if="isLineOwnedSkuRow(row)" type="info" size="mini" class="ml-1">
									{{ $t("cost_report.sku_from_order") }}
								</el-tag>
								<el-tag v-if="row.cost_missing" type="warning" size="mini" class="ml-1">
									{{ $t("cost_report.cost_missing") }}
								</el-tag>
								<el-tag v-else-if="row.missing_cost_sku > 0" type="warning" size="mini" class="ml-1">
									{{ row.missing_cost_sku }}
								</el-tag>
							</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.platform_product_id')" min-width="130" v-if="level === 'promotion'">
							<div slot-scope="{ row }">{{ row.platform_product_id }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.sku_code')" min-width="130" v-if="level === 'sku'">
							<div slot-scope="{ row }">{{ row.sku_code }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.seller_sku')" min-width="130" v-if="level === 'sku'">
							<div slot-scope="{ row }">{{ row.seller_sku }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.orders')" align="right" :min-width="expenseVisible ? 80 : 90" v-if="level !== 'sku'">
							<div slot-scope="{ row }">{{ row.orders }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.qty')" align="right" :min-width="expenseVisible ? 80 : 90">
							<div slot-scope="{ row }">{{ row.qty }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.sales')" align="right" :min-width="moneyColWidth">
							<div slot-scope="{ row }">{{ money(row.sales) }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.unit_cost')" align="right" min-width="110" v-if="level === 'sku'">
							<div slot-scope="{ row }">{{ money(row.unit_cost) }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.cost')" align="right" :min-width="moneyColWidth">
							<div slot-scope="{ row }">{{ money(row.cost) }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.margin')" align="right" :min-width="moneyColWidth">
							<div slot-scope="{ row }">{{ money(row.margin) }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.expense')" align="right" :min-width="moneyColWidth" v-if="expenseVisible">
							<div slot-scope="{ row }" class="text-danger">{{ money(row.expense) }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.net_profit')" align="right" :min-width="moneyColWidth" v-if="expenseVisible">
							<div slot-scope="{ row }" :class="rowNetProfit(row) < 0 ? 'text-danger' : ''">{{ money(rowNetProfit(row)) }}</div>
						</el-table-column>

						<el-table-column :label="$t('cost_report.margin_pct')" align="right" min-width="90">
							<div slot-scope="{ row }">{{ rowMarginPct(row) }}%</div>
						</el-table-column>
					</el-table>
				</div>
			</card>

			<!-- The blocking modal. It has no way out on purpose: the three
			     no-close props mean the ONLY thing that can hide it is `busy`
			     going false, which happens in a finally — success or error.
			     `append-to-body` keeps it above the card's stacking context. -->
			<el-dialog
				:visible.sync="busy"
				:close-on-click-modal="false"
				:close-on-press-escape="false"
				:show-close="false"
				:modal-append-to-body="true"
				append-to-body
				center
				width="280px"
				custom-class="cost-report-loading"
			>
				<div class="text-center py-2">
					<i class="el-icon-loading" style="font-size: 32px"></i>
					<div class="mt-3">{{ $t("cost_report.loading") }}</div>
				</div>
			</el-dialog>
		</div>
	</div>
</template>

<script>
// ReportProductCost.vue — รายงานต้นทุน (spec 2026-09-22 §7.2, issue #265).
//
// One screen, three levels, ONE set of filters: Platform|ช่องทางการขาย ×
// date range. Clicking a row drills down; the breadcrumb climbs back. Each
// level is a separate GET, so nothing is aggregated in the browser and the
// number on the screen is the number the SQL produced (the invariants of §8
// are properties of the API, and this page must not re-derive any of them).
//
// EVERYTHING THAT CAN BE WRONG LIVES IN src/utils/costReport.js — the drill
// state machine, the totals reducer, both sheet builders and the file name —
// because .vue files cannot be mounted under this repo's jest
// (reference-bo-fn-jest-infra). What stays here is markup and wiring.
import { Table, TableColumn, RadioGroup, RadioButton, Tag, DatePicker, Dialog } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { utils, writeFile } from "xlsx";
import ReportProvider from "@/resource/report";
import {
	LEVEL_ROOT,
	LEVEL_PROMOTION,
	LEVEL_SKU,
	initialDrill,
	drillToPromotion,
	drillToSku,
	drillUp,
	breadcrumbItems,
	queryParams,
	orderExportParams,
	summarySheet,
	showExpense,
	netProfit,
	orderSheet,
	exportFileName,
	marginPct,
	envelopeData,
	isTooManyLines,
	costReportErrorI18nKey,
	rangeErrorCode,
	defaultCostRange,
	disabledFromDate,
	disabledToDate,
	backfillParams,
	backfillResult,
	isLineOwnedSku,
	busyHoldMs,
} from "@/utils/costReport";

const report = new ReportProvider();

export default {
	components: {
		RouteBreadCrumb,
		[Table.name]: Table,
		[TableColumn.name]: TableColumn,
		[RadioGroup.name]: RadioGroup,
		[RadioButton.name]: RadioButton,
		[Tag.name]: Tag,
		[DatePicker.name]: DatePicker,
		[Dialog.name]: Dialog,
	},
	data() {
		// ONE call for both ends: two calls could straddle midnight in Bangkok
		// and open the screen on a four-day range.
		const range = defaultCostRange(new Date());
		return {
			view: "platform",
			// last three days, Bangkok — §7.2 said "since the 1st", the owner
			// asked for the default that matches how the report is actually used
			timeFrom: range.timeFrom,
			timeTo: range.timeTo,
			drill: initialDrill(),
			rows: [],
			total: { orders: 0, qty: 0, sales: 0, cost: 0, margin: 0, missing_cost_sku: 0 },
			costColumnsMissing: false,
			// the view the rows on screen were fetched with — the expense columns
			// follow THIS, not the radio, so they never show over channel rows
			loadedView: "",
			loading: false,
			exporting: false,
			backfilling: false,
			// busy drives the blocking modal. It is separate from loading /
			// exporting / backfilling (which only disable buttons) so that every
			// request that talks to the API from here — the backfill included —
			// has one flag to raise and one finally to lower.
			busy: false,
			busyAt: 0,
		};
	},
	computed: {
		level() {
			return this.drill.level;
		},
		// The calendars grey out whatever rangeErrorCode would refuse, so the
		// 7-day rule is visible before the click rather than as a dialog after
		// it. Each end is constrained by the OTHER end's current value.
		fromPickerOptions() {
			const timeTo = this.timeTo;
			return { disabledDate: (date) => disabledFromDate(date, timeTo) };
		},
		toPickerOptions() {
			const timeFrom = this.timeFrom;
			return { disabledDate: (date) => disabledToDate(date, timeFrom) };
		},
		breadcrumb() {
			return breadcrumbItems(this.drill, this.$t("cost_report.all"));
		},
		totalMarginPct() {
			return marginPct(this.total.sales, this.total.cost);
		},
		// รายจ่าย / กำไรสุทธิ: root level of view=platform only (owner 2026-10-26)
		expenseVisible() {
			return showExpense(this.level, this.loadedView);
		},
		totalNetProfit() {
			return netProfit(this.total);
		},
		// Nine columns at the theme's 24px cell padding wrapped every money
		// cell (and the %) onto two lines at 1366 (measured in headless
		// Chrome). With expense on, .cost-report-compact halves the padding and
		// the money columns get 120 → ~96px of text, enough for "1,234,567.89".
		moneyColWidth() {
			return this.expenseVisible ? 120 : 110;
		},
		tileClass() {
			return this.expenseVisible ? "col-6 col-md-3 col-xl" : "col-md-2";
		},
	},
	mounted() {
		this.reload();
	},
	methods: {
		rowNetProfit(row) {
			return netProfit(row);
		},
		// The blocking modal is raised and lowered ONLY through these two, so the
		// anti-flicker floor (COST_REPORT_BUSY_MIN_MS) applies to every request
		// and `busy` cannot be left on by a path that forgot the finally. The
		// wait is skipped when nothing is up.
		raiseBusy() {
			this.busyAt = Date.now();
			this.busy = true;
		},
		async lowerBusy() {
			if (!this.busy) return;
			const left = busyHoldMs(this.busyAt, Date.now());
			if (left > 0) await new Promise((r) => setTimeout(r, left));
			this.busy = false;
		},

		money(v) {
			const n = typeof v === "number" ? v : parseFloat(v);
			if (!isFinite(n)) return "0.00";
			return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
		},
		rowName(row) {
			if (this.level === LEVEL_SKU) return row.sku_name;
			return row.name;
		},
		rowMarginPct(row) {
			return marginPct(row.sales, row.cost);
		},
		isLineOwnedSkuRow(row) {
			return isLineOwnedSku(this.level, row);
		},
		params() {
			return queryParams(this.drill, {
				view: this.view,
				timeFrom: this.timeFrom,
				timeTo: this.timeTo,
			});
		},
		// showError translates the API's machine code (cost_report_bad_view,
		// cost_report_range_too_wide, …) — the raw Msg must never be a dialog
		// title. An unrecognised code becomes the generic title with the code
		// underneath, readable to support without pretending to be a sentence.
		showError(err) {
			const { key, detail } = costReportErrorI18nKey(err);
			const is403 = !!(err && err.response && err.response.status === 403);
			// http.js already opened a generic "ไม่มีสิทธิ์" dialog for every
			// 403. Replace it only when the API named the reason; for any other
			// 403 the global message is the accurate one.
			if (is403 && key !== "cost_report.errors.product_cost_forbidden") return;
			swal.fire({ title: this.$t(key), text: detail, icon: "error" });
		},
		// badRange is the pre-check: a range the API is certain to reject costs
		// the operator a round trip and shows up as a spinner that ends in a
		// dialog. The message is the SAME one the server's code maps to.
		badRange() {
			const code = rangeErrorCode(this.timeFrom, this.timeTo);
			if (!code) return false;
			swal.fire({ title: this.$t(`cost_report.errors.${code}`), icon: "warning" });
			return true;
		},
		// Switching Platform ↔ ช่องทางการขาย resets the drill: a platform id is
		// not a sale-channel id, and carrying the key across would silently show
		// one key's rows under the other's name.
		onViewChange() {
			this.drill = initialDrill();
			this.reload();
		},
		goToLevel(level) {
			this.drill = drillUp(this.drill, level);
			this.reload();
		},
		onRowClick(row) {
			if (this.level === LEVEL_ROOT) {
				this.drill = drillToPromotion(this.drill, row);
				this.reload();
				return;
			}
			if (this.level === LEVEL_PROMOTION) {
				this.drill = drillToSku(this.drill, row);
				this.reload();
			}
			// LEVEL_SKU is the bottom — a click there does nothing.
		},
		fetchLevel() {
			const p = this.params();
			if (this.level === LEVEL_ROOT) return report.ReportCostRoot(p);
			if (this.level === LEVEL_PROMOTION) return report.ReportCostPromotions(p);
			return report.ReportCostSkus(p);
		},
		async reload() {
			if (this.badRange()) {
				this.rows = [];
				return;
			}
			this.loading = true;
			this.raiseBusy();
			try {
				const res = await this.fetchLevel();
				const data = envelopeData(res);
				this.rows = (data.rows || []).map((r, i) => ({ ...r, _rowKey: `${this.level}-${i}` }));
				this.total = Object.assign(
					{ orders: 0, qty: 0, sales: 0, cost: 0, margin: 0, missing_cost_sku: 0 },
					data.total || {}
				);
				this.costColumnsMissing = data.cost_columns_missing === true;
				this.loadedView = data.view || this.view;
				// The SKU level carries the product's name for the breadcrumb.
				if (this.level === LEVEL_SKU && data.bo_product_name) {
					this.drill = { ...this.drill, boProductName: data.bo_product_name };
				}
			} catch (err) {
				// Down goes the modal BEFORE the error dialog: el-dialog's
				// overlay sits at z-index 2000+ and sweetalert2's at 1060, so a
				// swal opened under it would be a greyed-out screen with an
				// invisible message behind it. The finally below is still the
				// guarantee — this line is only about the order of the two.
				await this.lowerBusy();
				this.showError(err);
				this.rows = [];
			} finally {
				await this.lowerBusy();
				this.loading = false;
			}
		},
		// backfillCost re-snapshots bo_orders_item.unit_cost for the range that is
		// on the screen (§13.7 · bo-api #599 §13.5).
		//
		// THREE THINGS ARE DELIBERATE HERE.
		//
		// The confirmation prints the dates BACK OUT. This endpoint rewrites
		// history: an executive who printed this month last week will get a
		// different number afterwards, and the one thing that must never happen is
		// somebody pressing it believing it applied to a narrower range than it did.
		//
		// It sends the DATES ONLY, never the drill state. The endpoint repairs
		// every line of the range; narrowing it to the platform being looked at
		// would leave the rest half repaired and the totals would stop reconciling
		// with the levels beneath them.
		//
		// It reloads afterwards. Without that, the numbers on the screen are the
		// ones from before the repair, sitting under a dialog that just said the
		// repair succeeded — the most convincing way to make a working button look
		// broken.
		async backfillCost() {
			if (this.badRange()) return;

			const confirmed = await swal.fire({
				title: this.$t("cost_report.backfill"),
				text: this.$t("cost_report.backfill_confirm", { from: this.timeFrom, to: this.timeTo }),
				icon: "warning",
				showCancelButton: true,
				confirmButtonText: this.$t("confirm"),
				cancelButtonText: this.$t("cancel"),
			});
			if (!confirmed || !confirmed.value) return;

			this.backfilling = true;
			// The repair is the LONGEST request this screen makes, so it is the one
			// that most needs the blocking modal: the confirm dialog is already
			// closed, the page looks idle, and a second press would start a second
			// rewrite of the same range.
			this.raiseBusy();
			try {
				const res = await report.ProductCostBackfillOrders(
					backfillParams({ timeFrom: this.timeFrom, timeTo: this.timeTo })
				);
				const out = backfillResult(res);
				// Modal down before ANY swal, same z-index reason as reload():
				// el-dialog's overlay is 2000+ and sweetalert2's is 1060, so a
				// result dialog opened under it is an invisible message behind a
				// grey screen. The finally is still the guarantee.
				await this.lowerBusy();
				// `skipped` is the un-migrated database answering 200 with four
				// zeros. Saying "scanned 0 · updated 0" there would read like "there
				// was nothing to do", which is the opposite of the truth.
				if (out.skipped) {
					swal.fire({ title: this.$t("cost_report.cost_columns_missing"), icon: "warning" });
					return;
				}
				await swal.fire({
					title: this.$t("cost_report.backfill"),
					text: this.$t("cost_report.backfill_done", {
						scanned: out.scanned,
						updated: out.updated,
						missing: out.missing,
						unresolved: out.unresolved,
					}),
					icon: "success",
				});
				// reload() raises and lowers `busy` itself for the refetch.
				await this.reload();
			} catch (err) {
				await this.lowerBusy();
				this.showError(err);
			} finally {
				await this.lowerBusy();
				this.backfilling = false;
			}
		},
		// Sheet 1 is the table on the screen; sheet 2 is every order line behind
		// it. If sheet 2 cannot be fetched (413) NO file is written at all — a
		// workbook with only the summary in it looks complete and is not (§7.2).
		async exportExcel() {
			if (this.badRange()) return;
			this.exporting = true;
			this.raiseBusy();
			try {
				const labels = {
					name: this.$t("cost_report.name"),
					platform_product_id: this.$t("cost_report.platform_product_id"),
					sku_code: this.$t("cost_report.sku_code"),
					seller_sku: this.$t("cost_report.seller_sku"),
					orders: this.$t("cost_report.orders"),
					qty: this.$t("cost_report.qty"),
					sales: this.$t("cost_report.sales"),
					cost: this.$t("cost_report.cost"),
					unit_cost: this.$t("cost_report.unit_cost"),
					margin: this.$t("cost_report.margin"),
					margin_pct: this.$t("cost_report.margin_pct"),
					expense: this.$t("cost_report.expense"),
					net_profit: this.$t("cost_report.net_profit"),
					missing_cost_sku: this.$t("cost_report.missing_cost_sku"),
					cost_missing: this.$t("cost_report.cost_missing"),
					yes: this.$t("cost_report.yes"),
				};

				let lines = [];
				try {
					// At the root this sends NO key on purpose: …/cost/orders
					// answers an unkeyed request with every line in the range,
					// and `key: 0` would mean ไม่ระบุ — one channel — so sheet 2
					// would be a subset of the summary printed beside it.
					const p = orderExportParams(this.drill, {
						view: this.view,
						timeFrom: this.timeFrom,
						timeTo: this.timeTo,
					});
					const res = await report.ReportCostOrders(p);
					lines = envelopeData(res).rows || [];
				} catch (err) {
					if (isTooManyLines(err)) {
						// same z-index reason as in reload(): the modal has to be
						// gone before a swal is opened. `return` still lands in
						// the outer finally, which lowers the flag for good.
						await this.lowerBusy();
						swal.fire({ title: this.$t("cost_report.too_many_lines"), icon: "warning" });
						return;
					}
					throw err;
				}

				const wb = utils.book_new();
				utils.book_append_sheet(wb, utils.json_to_sheet(summarySheet(this.level, this.rows, labels, { withExpense: this.expenseVisible })), "summary");
				utils.book_append_sheet(wb, utils.json_to_sheet(orderSheet(lines, labels.yes)), "orders");
				writeFile(
					wb,
					exportFileName({
						view: this.view,
						level: this.level,
						timeFrom: this.timeFrom,
						timeTo: this.timeTo,
					})
				);
			} catch (err) {
				await this.lowerBusy();
				this.showError(err);
			} finally {
				await this.lowerBusy();
				this.exporting = false;
			}
		},
	},
};
</script>

<style>
/* root level of view=platform only (รายจ่าย + กำไรสุทธิ add two columns) */
.cost-report-compact .el-table th,
.cost-report-compact .el-table td {
	padding-left: 12px !important;
	padding-right: 12px !important;
}
</style>
